import LoadingSpinner from "@components/loadingSpinner";
import { FormattedPrice } from "@features/eventDisplayGA/types";
import {
  SparklesIcon,
  XMarkIcon as XMarkIconSmall,
  ChevronDownIcon,
} from "@heroicons/react/20/solid";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Badge, Text } from "@components/theming";
import { Select } from "@components/inputs";
import { Value, Icon } from "@radix-ui/react-select";
import { useNotificationStore } from "@store/notificationStore";
import { PriceStatus } from "@utils/enums";
import { joinClassNames } from "@utils/helpers";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useIsShopsite } from "@context/DomainContext";
import { useThemeContext } from "@utils/hooks";

interface Props {
  price: FormattedPrice;
  putQtyInCart: (
    price: FormattedPrice,
    qty: string,
    isDynamic?: boolean
  ) => Promise<"success" | "failure" | { status: string; retry_after: number }>;
  selectedQuantities: { id: string; qty: number; qty_from_combo: number }[];
}

export default function AddTicketDropdown({
  price,
  putQtyInCart,
  selectedQuantities,
}: Props) {
  const theme = useThemeContext();
  const t = useTranslations("EventPage.gaselection");
  const setFailure = useNotificationStore((state) => state.setFailure);
  const [priceLoading, setPriceLoading] = useState<{
    id: number;
    status: "loading" | "success" | "failure";
  } | null>(null);
  const selectedQty = selectedQuantities.find((sq) => sq.id === price.id);
  const isShopsite = useIsShopsite();

  const option_array = [
    0,
    ...Array.from(
      {
        length:
          (price.bookingLimit ?? price.max) -
          price.min +
          (price.min === 0 ? 0 : 1) +
          (price.bookingLimit && price.bookingLimit < price.max && selectedQty
            ? selectedQty.qty
            : 0),
      },
      (_, index) => price.min + index + (price.min === 0 ? 1 : 0)
    ),
  ];

  async function handleValueChange(qty: string, retry_attempts = 0) {
    if (retry_attempts > 5) {
      setPriceLoading({
        id: price.priceAgeId
          ? price.priceAgeId
          : price.comboId
          ? price.comboId
          : 0,
        status: "failure",
      });
      setTimeout(() => setPriceLoading(null), 1000);
      setFailure(t("retryfailednotification"));
      return;
    }
    setPriceLoading({
      id: price.priceAgeId
        ? price.priceAgeId
        : price.comboId
        ? price.comboId
        : 0,
      status: "loading",
    });
    const result = await putQtyInCart(price, qty, false);
    if (typeof result !== "string" && result.status === "retry") {
      setTimeout(
        () => handleValueChange(qty, retry_attempts + 1),
        result.retry_after * 1000
      );
    } else {
      setPriceLoading({
        id: price.priceAgeId
          ? price.priceAgeId
          : price.comboId
          ? price.comboId
          : 0,
        status: typeof result !== "string" ? "failure" : result,
      });
      setTimeout(() => setPriceLoading(null), 1000);
    }
  }

  return (
    <div className="relative w-16">
      <Select
        value={selectedQty?.qty.toString() ?? ""}
        onValueChange={handleValueChange}
        disabled={
          (price.priceAgeId && priceLoading?.id === price.priceAgeId) ||
          (price.comboId && priceLoading?.id === price.comboId) ||
          (price.isCombo
            ? price.max === 0 || price.max === price.bookedTickets
            : (price.status !== PriceStatus.AVAILABLE &&
                selectedQty?.qty === 0) ||
              (price.bookingLimit ?? price.max) < price.min)
        }
        triggerContent={
          <>
            <span className="sr-only">
              {t("selectquantity", { type: price.type })}
            </span>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.div
                className={joinClassNames(
                  "flex items-center justify-center",
                  isShopsite ? "gap-1" : ""
                )}
                key={
                  (price.priceAgeId && priceLoading?.id === price.priceAgeId) ||
                  (price.comboId && priceLoading?.id === price.comboId)
                    ? priceLoading?.status
                    : "quantity"
                }
                initial={{ opacity: 0, width: "auto" }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: "auto" }}
                transition={{
                  duration: 0.15,
                  delay: 0.15,
                }}
              >
                {(price.priceAgeId && priceLoading?.id === price.priceAgeId) ||
                (price.comboId && priceLoading?.id === price.comboId) ? (
                  priceLoading?.status === "loading" ? (
                    <LoadingSpinner
                      data-testid="priceloading"
                      size={isShopsite ? "16" : "12"}
                      color={
                        theme && theme.appearance === "light"
                          ? "#000"
                          : theme && theme.appearance === "dark"
                          ? "#fff"
                          : undefined
                      }
                      classes={isShopsite ? "w-7" : "w-4"}
                    />
                  ) : priceLoading?.status === "success" ? (
                    <CheckIcon
                      className={
                        isShopsite
                          ? "w-7 h-4 text-emerald-800"
                          : "w-4 h-4 text-[color:var(--green-a11)] stroke-2"
                      }
                    />
                  ) : (
                    <XMarkIcon
                      className={
                        isShopsite
                          ? "w-7 h-4 text-red-800"
                          : "w-4 h-4 text-[color:var(--red-a11)]"
                      }
                    />
                  )
                ) : isShopsite ? (
                  <>
                    <Value>{selectedQty?.qty.toString()}</Value>
                    <Icon>
                      <ChevronDownIcon className="w-4 h-4" />
                    </Icon>
                  </>
                ) : (
                  <>{selectedQty?.qty.toString()}</>
                )}
              </motion.div>
            </AnimatePresence>
            <motion.div
              className="absolute -top-2 -right-2"
              animate={
                ((price.priceAgeId && priceLoading?.id === price.priceAgeId) ||
                  (price.comboId && priceLoading?.id === price.comboId)) &&
                priceLoading?.status !== "loading"
                  ? { scale: [0, 1, 1.2, 1, 0], rotate: [0, 2, 4, 6, 8] }
                  : { scale: 0, rotate: 0 }
              }
              transition={{ duration: 1.5 }}
            >
              {priceLoading?.status === "success" ? (
                <SparklesIcon
                  className={joinClassNames(
                    "h-4 w-4",
                    isShopsite
                      ? "text-yellow-400"
                      : "text-[color:var(--amber-9)]"
                  )}
                />
              ) : priceLoading?.status === "failure" ? (
                <XMarkIconSmall
                  className={joinClassNames(
                    "h-4 w-4",
                    isShopsite ? "text-red-400" : "text-[color:var(--red-9)]"
                  )}
                />
              ) : (
                <></>
              )}
            </motion.div>
            <motion.div
              className="absolute bottom-0 -left-3"
              animate={
                ((price.priceAgeId && priceLoading?.id === price.priceAgeId) ||
                  (price.comboId && priceLoading?.id === price.comboId)) &&
                priceLoading?.status !== "loading"
                  ? { scale: [0, 1, 1.2, 1, 0], rotate: [0, -3, -6, -9, -12] }
                  : { scale: 0, rotate: 0 }
              }
              transition={{ duration: 1.5 }}
            >
              {priceLoading?.status === "success" ? (
                <SparklesIcon
                  className={joinClassNames(
                    "h-4 w-4",
                    isShopsite
                      ? "text-yellow-400"
                      : "text-[color:var(--amber-9)]"
                  )}
                />
              ) : priceLoading?.status === "failure" ? (
                <XMarkIconSmall
                  className={joinClassNames(
                    "h-4 w-4",
                    isShopsite ? "text-red-400" : "text-[color:var(--red-9)]"
                  )}
                />
              ) : (
                <></>
              )}
            </motion.div>
            {priceLoading &&
            priceLoading.status !== "loading" &&
            ((price.priceAgeId && priceLoading?.id === price.priceAgeId) ||
              (price.comboId && priceLoading?.id === price.comboId)) ? (
              <>
                <motion.div
                  className={joinClassNames(
                    "absolute -top-2 -left-4 w-2.5 h-2.5 rounded-[3px]",
                    priceLoading.status === "success" && isShopsite
                      ? "bg-emerald-200"
                      : isShopsite
                      ? "bg-red-200"
                      : priceLoading.status === "success"
                      ? "bg-[color:var(--green-10)]"
                      : "bg-[color:var(--red-10)]"
                  )}
                  initial={{ x: 0, y: 0, scale: 0, rotate: 0 }}
                  animate={{
                    x: [0, -2, -4, -5, -5],
                    y: [0, -2, -4, -5, -5],
                    scale: [0, 1, 1.2, 1, 0],
                    rotate: [0, -3, -6, -9, -12],
                  }}
                  transition={{ duration: 1.5 }}
                />
                <motion.div
                  className={joinClassNames(
                    "absolute -bottom-2 right-2 w-2.5 h-2.5 rounded-[3px]",
                    priceLoading.status === "success" && isShopsite
                      ? "bg-emerald-700"
                      : isShopsite
                      ? "bg-red-700"
                      : priceLoading.status === "success"
                      ? "bg-[color:var(--green-4)]"
                      : "bg-[color:var(--red-4)]"
                  )}
                  initial={{ x: 0, y: 0, scale: 0, rotate: 0 }}
                  animate={{
                    x: [0, 2, 4, 5, 5],
                    y: [0, 4, 8, 11, 11],
                    scale: [0, 1, 1.2, 1, 0],
                    rotate: [0, 3, 6, 9, 12],
                  }}
                  transition={{ duration: 1.5 }}
                />
                <motion.div
                  className={joinClassNames(
                    "absolute -top-2 right-2 w-2 h-2 rounded",
                    priceLoading.status === "success" && isShopsite
                      ? "bg-emerald-100"
                      : isShopsite
                      ? "bg-red-100"
                      : priceLoading.status === "success"
                      ? "bg-[color:var(--green-11)]"
                      : "bg-[color:var(--red-11)]"
                  )}
                  initial={{ x: 0, y: 0, scale: 0, rotate: 0 }}
                  animate={{
                    x: [0, 2, 4, 5, 5],
                    y: [0, -3, -6, -8, -8],
                    scale: [0, 1, 1.2, 1, 0],
                    rotate: [0, 3, 6, 9, 12],
                  }}
                  transition={{ duration: 1.5 }}
                />
              </>
            ) : (
              <></>
            )}
          </>
        }
        whitelabelButtonClasses={joinClassNames(
          "relative w-full",
          (price.priceAgeId && priceLoading?.id === price.priceAgeId) ||
            (price.comboId && priceLoading?.id === price.comboId)
            ? priceLoading?.status === "loading"
              ? "bg-[color:var(--gray-a3)] border-[color:var(--gray-8)]"
              : priceLoading?.status === "success"
              ? "bg-[color:var(--green-a3)] border-[color:var(--green-9)]"
              : "bg-[color:var(--red-a3)] border-[color:var(--red-9)]"
            : ""
        )}
        shopsiteButtonClasses={joinClassNames(
          "!justify-center !px-4 !duration-200 relative px-4 text-sm leading-none text-black !rounded disabled:!opacity-100",
          (price.priceAgeId && priceLoading?.id === price.priceAgeId) ||
            (price.comboId && priceLoading?.id === price.comboId)
            ? priceLoading?.status === "loading"
              ? "!bg-gray-100 !border-gray-300"
              : priceLoading?.status === "success"
              ? "!bg-emerald-100 !border-emerald-400"
              : "!bg-red-100 !border-red-400"
            : "disabled:bg-gray-200 !disabled:hover:bg-gray-200 disabled:text-gray-500"
        )}
        sharedButtonClasses="transition-colors ease-in-out duration-200"
        buttonVariant="surface"
        options={option_array.map((qty) => ({
          label: qty,
          value: qty.toString(),
        }))}
      />
      {!(selectedQty?.id || "").includes("combo") &&
      (selectedQty?.qty_from_combo || 0) > 0 ? (
        <Badge
          sharedClasses="absolute"
          whitelabelClasses="-right-1 top-[calc(100%+3px)]"
          shopsiteClasses="-right-0.5 sm:-right-2 top-full sm:top-[calc(100%+3px)] !px-1 !py-0 !rounded-lg w-max text-xs"
        >
          <Text as="span" size="1" weight="medium">
            {t("amountfromcombo", {
              amount: selectedQty?.qty_from_combo.toString(),
            })}
          </Text>
        </Badge>
      ) : (
        <></>
      )}
    </div>
  );
}
